import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Flame, Trophy, Target } from '@phosphor-icons/react';
import axios from 'axios';
import { API_URL } from '../config/api';

/**
 * Streak Tile — "addictive game" layer on top of the Activity Log.
 * Shows current streak (flame), personal best (trophy), this-month plan progress (ring).
 * Renders nothing if the user has never logged any activity (don't guilt empty accounts).
 */
export const StreakTile = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/activity-streak`, { withCredentials: true });
        setData(res.data);
      } catch {
        // silent failure — streak tile is additive, never blocks the page
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading || !data) return null;
  // Don't show the tile for brand-new users with no activity at all
  if (data.total_days_active === 0) return null;

  const { current_streak, longest_streak, plan_actions_this_month, plan_total_this_month, milestone_copy, is_live_today } = data;
  const planPct = plan_total_this_month > 0
    ? Math.round((plan_actions_this_month / plan_total_this_month) * 100)
    : 0;

  return (
    <Card data-testid="streak-tile" className="overflow-hidden border-orange-200/60 dark:border-orange-900/40 bg-gradient-to-br from-orange-50 via-amber-50 to-white dark:from-orange-950/30 dark:via-amber-950/20 dark:to-transparent">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center gap-4">
          {/* Flame + streak number */}
          <div className="flex items-center gap-3 shrink-0" data-testid="streak-flame">
            <div className={`relative w-14 h-14 rounded-2xl flex items-center justify-center ${is_live_today ? 'bg-gradient-to-br from-orange-500 to-red-500' : 'bg-slate-400 dark:bg-slate-600'}`}>
              <Flame size={28} weight="fill" className="text-white" />
              {is_live_today && current_streak >= 3 && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse" />
              )}
            </div>
            <div>
              <div className="flex items-baseline gap-1">
                <span data-testid="current-streak-value" className="text-3xl font-bold tabular-nums text-orange-700 dark:text-orange-300">
                  {current_streak}
                </span>
                <span className="text-xs font-medium text-orange-700/80 dark:text-orange-300/80">
                  {current_streak === 1 ? 'day' : 'days'}
                </span>
              </div>
              <div className="text-[11px] uppercase tracking-wide font-semibold text-orange-900/60 dark:text-orange-200/60">
                Current streak
              </div>
            </div>
          </div>

          {/* Separator + stats */}
          <div className="flex-1 flex items-center justify-end gap-4 sm:gap-6 min-w-0">
            <div className="hidden sm:flex flex-col items-end" data-testid="longest-streak">
              <div className="flex items-center gap-1 text-sm font-semibold text-amber-700 dark:text-amber-300">
                <Trophy size={14} weight="fill" /> {longest_streak}
              </div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Personal best</div>
            </div>

            {plan_total_this_month > 0 && (
              <div className="flex items-center gap-2" data-testid="plan-progress">
                {/* Progress ring */}
                <svg width="44" height="44" viewBox="0 0 44 44" className="shrink-0 -rotate-90">
                  <circle cx="22" cy="22" r="18" fill="none" stroke="currentColor" strokeWidth="4" className="text-emerald-100 dark:text-emerald-900/40" />
                  <circle
                    cx="22" cy="22" r="18" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round"
                    className="text-emerald-500 dark:text-emerald-400 transition-all duration-700"
                    strokeDasharray={`${2 * Math.PI * 18}`}
                    strokeDashoffset={`${2 * Math.PI * 18 * (1 - planPct / 100)}`}
                  />
                </svg>
                <div>
                  <div className="flex items-center gap-1 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                    <Target size={14} weight="fill" /> {plan_actions_this_month}/{plan_total_this_month}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Plan this month</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Milestone copy line */}
        <p data-testid="streak-milestone-copy" className="mt-3 text-xs text-orange-900/70 dark:text-orange-200/70 italic">
          {milestone_copy}
        </p>
      </CardContent>
    </Card>
  );
};
